import { useOutsideAlerter } from '@hooks';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '../Icon/Icon';

import styles from './Accordion.module.scss';

interface AccordionProps {
  className?: string;
  label: string | React.ReactNode;
  numberIndicator?: number;
  disableOneAtTime?: boolean;
  iconToLeft?: boolean;
  defaultIsOpen?: boolean;
  /** Fired the first time the accordion transitions to open. Use for lazy-loading content. */
  onFirstOpen?: () => void;
}

export const Accordion: React.FC<React.PropsWithChildren<AccordionProps>> = ({
  className,
  label,
  numberIndicator,
  disableOneAtTime = false,
  iconToLeft = false,
  children,
  defaultIsOpen = false,
  onFirstOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasFiredFirstOpenRef = useRef(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>();
  const { pathname } = useRouter();

  const accordionRef = useRef<HTMLDivElement | null>(null);
  useOutsideAlerter(accordionRef, e => {
    if ((e.target as HTMLDivElement).getAttribute('data-type') === 'accordion' && !disableOneAtTime) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    const ro = new ResizeObserver(entries => {
      setContentHeight(entries[0].target.scrollHeight); // if content height changes then update contentHeight
    });
    ro.observe(contentRef.current as HTMLDivElement);
  }, []);

  useEffect(() => {
    if (defaultIsOpen) {
      setIsOpen(defaultIsOpen);
    }
  }, [defaultIsOpen]);

  useEffect(() => {
    if (isOpen && !hasFiredFirstOpenRef.current) {
      hasFiredFirstOpenRef.current = true;
      onFirstOpen?.();
    }
  }, [isOpen, onFirstOpen]);

  const isReport = useMemo(() => pathname.startsWith('/report'), [pathname]);

  useEffect(() => {
    if (isReport) {
      setIsOpen(true);
    }
  }, [isReport]);

  return (
    <div className={clsx(styles.accordion, { [styles.isOpen]: isOpen, [styles.isReport]: isReport }, className)}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={styles.header}
        ref={accordionRef}
        data-type="accordion"
      >
        <div className={styles.labelWrapper}>
          {iconToLeft && (
            <div
              data-type="accordion"
              className={styles.icon}
            >
              <Icon
                data-type="accordion"
                name="angle-right"
                style={{ transform: isOpen ? 'rotate(270deg)' : 'rotate(90deg)' }}
              />
            </div>
          )}
          <span
            data-type="accordion"
            className={styles.label}
          >
            {label}
          </span>
          {numberIndicator !== undefined && numberIndicator > 0 && (
            <span className={clsx(styles.indicator, { [styles.show]: !isOpen })}>{numberIndicator}</span>
          )}
        </div>
        {!iconToLeft && (
          <div
            data-type="accordion"
            className={styles.icon}
          >
            <Icon
              data-type="accordion"
              name="angle-right"
              style={{ transform: isOpen ? 'rotate(270deg)' : 'rotate(90deg)' }}
            />
          </div>
        )}
      </div>
      <div
        className={clsx(styles.contentWrapper)}
        style={{
          height: isOpen ? contentHeight ?? 'auto' : 0,
          overflow: isOpen ? 'auto' : 'hidden',
        }}
      >
        <div
          className={styles.content}
          ref={contentRef}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
